import * as R from 'ramda';
import * as P from 'plow-js';
// features
import PC from '../features/permission/role-permission';
// constants
import * as GC from '../constants';
// helpers
import {
  isBetween,
  convertTimeToFormat,
  convertInstanceToDefaultDateFormat,
} from './date';
import { createStringFromArray } from './string';
import { toNumber, mathRoundNumber } from './calc';
import { stringIncludesFilterValue } from './filter';
import { getPropFromObject, getGuidFromObject } from './getter';
import { getGroupedByCountryStateOptionsByConfig } from './options';
import { getItemFromLocalStorage, setItemToLocalStorage } from './storage';
import { getWindowLocale, getWindowLocaleArr, getWindowLocaleFromStringOrArray } from './locale';
import {
  getItemFromWindow,
  hasNotAmousCurrentUserPermissions,
  getAmousDropdownConfigOptionsByNameFromWindow,
} from './window';
import {
  isTrue,
  ifElse,
  isArray,
  isString,
  notEquals,
  getOrElse,
  isAnyTrue,
  isFunction,
  isNotEmpty,
  mapIndexed,
  notContain,
  isNilOrEmpty,
  isAnyNilOrEmpty,
  isNotNilAndNotEmpty,
} from './helpers';
//////////////////////////////////////////////////

const getDefaultReportModal = (component, maxWidth = 1470) => ({
  component,
  options: {
    maxWidth,
    width: 'calc(100vw - 20px)',
    height: 'calc(100vh - 60px)',
  },
});

const isFilterNumberRange = ({ dataType, operation }) => R.and(
  R.equals(dataType, 'number'),
  R.equals(operation, 'range'),
);

const isFilterStringRange = ({ dataType, operation }) => R.and(
  R.equals(dataType, 'string'),
  R.equals(operation, 'range'),
);

const isFilterValuesBoolean = ({ dataType, operation }) => R.and(
  R.equals(dataType, 'boolean'),
  R.or(R.equals(operation, 'equal'), (R.equals(operation, 'not_equal'))),
);

const setOptionLabelFromNameOrValue = (options) => R.map(
  (option) => {
    const { name, label, value } = option;

    if (isNotNilAndNotEmpty(label)) return option;

    return R.assoc('label', R.or(name, value), option);
  },
  R.or(options, []),
);

const getReportColumnPropTextFromData = (propName, data) => {
  let text = R.pathOr('', [propName], data);

  if (isArray(text)) {
    text = R.join(', ', text);
  }

  return text;
};

const localeKeyToReportFieldMap = R.compose(
  R.indexBy(R.prop('locale')),
  R.values,
  R.mapObjIndexed((item, key) => {
    const { name } = item;
    const locale = R.trim(getWindowLocaleArr(name));

    return { key, locale };
  }),
);

const prependFieldToTableReportFields = (report, field) => {
  const fields = getPropFromObject('fields', report);

  if (isNilOrEmpty(fields)) return report;

  const newFields = R.compose(
    R.map((item) => R.assoc('sequence', R.inc(item.sequence), item)),
    R.prepend(field),
  )(fields);

  return R.assoc('fields', newFields, report);
};

const addSystemFieldsToTableReport = (report, systemFields) => {
  if (isNilOrEmpty(report)) return report;

  if (isNilOrEmpty(systemFields)) return report;

  const newFields = systemFields.map((name, index) => ({ name, sequence: index }));

  return R.assoc('systemFields', newFields, report);
};

const defaultFilterFields = {
  to: null,
  next: null,
  last: null,
  from: null,
  guid: null,
  groupId: null,
  timeUnit: null,
  dataType: null,
  operation: null,
  stringValue: null,
  stringRange: null,
  numberRange: null,
  numberValue: null,
  booleanValue: null,
  propertyName: null,
  dateRelative: false,
  referenceName: null,
  referenceValue: null,
};

const createReportFilterFromTitleSearch = (data) => {
  const { value, groupId, subGroupId, fieldProps } = data;

  const { type, options, collection, shouldRound, getFieldUiValue } = fieldProps;

  let filter;

  const propertyName = R.prop(GC.FIELD_VALUE, fieldProps);

  if (R.equals(type, 'string')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: type,
      getFieldUiValue,
      stringValue: value,
      collection: R.or(collection, false),
      operation: ifElse(isNotNilAndNotEmpty(options), 'in', 'contain'),
    };
  }

  if (R.equals(type, 'number')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      shouldRound,
      propertyName,
      dataType: type,
      getFieldUiValue,
      operation: 'equal',
      numberValue: value,
      collection: R.or(collection, false),
    };
  }

  if (R.equals(type, 'date')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: type,
      getFieldUiValue,
      operation: 'date-range',
      to: `${value} 11:59 PM`,
      from: `${value} 12:00 AM`,
      collection: R.or(collection, false),
    };
  }

  if (R.equals(type, 'time')) {
    const timeRange = R.is(Object, value) ? value : { to: null, from: value };
    const hasTimeRangeValue = R.any(isNotNilAndNotEmpty, R.values(timeRange));

    const timeRangeToUse = ifElse(
      hasTimeRangeValue,
      {
        to: R.or(R.prop('to', timeRange), '11:59 PM'),
        from: R.or(R.prop('from', timeRange), '12:00 AM'),
      },
      timeRange,
    );

    const getTimeFilterValue = (time) => {
      if (isNilOrEmpty(time)) return null;

      const valueToFormat = R.compose(R.join(' '), R.takeLast(2), R.split(' '))(time);

      return `${GC.DEFAULT_FILTER_DATE} ${convertTimeToFormat(valueToFormat, GC.DEFAULT_TIME_FORMAT)}`;
    };

    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: 'date',
      getFieldUiValue,
      operation: 'range',
      collection: R.or(collection, false),
      to: getTimeFilterValue(R.prop('to', timeRangeToUse)),
      from: getTimeFilterValue(R.prop('from', timeRangeToUse)),
    };
  }

  if (R.equals(type, 'dateOnly')) {
    const dateValue = isNilOrEmpty(value) ? value : convertInstanceToDefaultDateFormat(value);

    filter = {
      ...defaultFilterFields,
      to: null,
      groupId,
      subGroupId,
      propertyName,
      getFieldUiValue,
      dataType: 'date',
      operation: 'equal',
      collection: R.or(collection, false),
      from: isNilOrEmpty(dateValue) ? dateValue : `${dateValue} ${GC.DEFAULT_FILTER_TIME}`,
    };
  }

  if (R.equals(type, 'boolean')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: type,
      getFieldUiValue,
      operation: 'equal',
      booleanValue: value,
      collection: R.or(collection, false),
    };
  }

  if (R.equals(type, 'reference')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      dataType: type,
      getFieldUiValue,
      collection: false,
      operation: 'contain',
      referenceValue: value,
      propertyName: 'references',
      referenceName: propertyName,
    };
  }

  if (R.equals(type, 'selectMultiple')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: type,
      getFieldUiValue,
      operation: 'in',
      stringValue: value,
      collection: R.or(collection, false),
      selectMultipleValue: R.of(Array, { value }),
    };
  }

  if (R.equals(type, 'selectMultipleCreatable')) {
    filter = {
      ...defaultFilterFields,
      groupId,
      subGroupId,
      propertyName,
      dataType: type,
      getFieldUiValue,
      operation: 'in',
      stringValue: value,
      collection: R.or(collection, false),
      selectMultipleValue: R.of(Array, { value }),
    };
  }

  const isTimeRangeEmpty = R.and(
    R.equals(type, 'time'),
    R.is(Object, value),
  ) && R.all(isNilOrEmpty, R.values(value));

  return {
    filter,
    isEmptyValue: R.or(isNilOrEmpty(value), isTimeRangeEmpty),
  };
};

const handleTableTitleFilter = ({
  setTableTitleSort,
  getItemListRequest,
  setTableTitleFilter,
  resetListAndPagination,
}) => (data) => {
  const { isSorting } = data;

  if (isSorting) {
    setTableTitleSort(data.sortData);
  } else {
    const filter = createReportFilterFromTitleSearch(data);

    setTableTitleFilter(filter);
  }

  resetListAndPagination();

  setTimeout(() => getItemListRequest(true), 10);
};

const handleTableTitleFilter2 = (data, A, dispatch) => {
  const { isSorting } = data;

  dispatch(A.resetListAndPagination());

  if (isSorting) {
    dispatch(A.setTableTitleSort(data.sortData));
  } else {
    const filter = createReportFilterFromTitleSearch(data);

    dispatch(A.setTableTitleFilter(filter));
  }
};

const getTableTitleSortValues = (titleSortValues, data) => {
  const { name } = data;

  let sortValues;

  if (R.pathEq(R.prop(GC.FIELD_ORDER, data), [name, GC.FIELD_ORDER], titleSortValues)) {
    sortValues = R.compose(
      R.indexBy(R.prop(GC.FIELD_NAME)),
      mapIndexed((entity, i) => R.assoc('sequence', R.inc(i), entity)),
      R.sortBy(R.prop('sequence')),
      R.values,
      R.dissoc(name),
    )(titleSortValues);
  } else {
    let sequence;

    const exist = R.path([name], titleSortValues);

    if (isNotNilAndNotEmpty(exist)) {
      sequence = R.prop('sequence', exist);
    } else {
      sequence = R.compose(
        R.inc,
        R.length,
        R.values,
      )(titleSortValues);
    }

    sortValues = R.assoc(name, R.assoc('sequence', sequence, data), titleSortValues);
  }

  return sortValues;
};

const setTableTitleSort = (state, data) => {
  const { titleSortValues } = state;
  const sortValues = getTableTitleSortValues(titleSortValues, data);

  return P.$set('titleSortValues', sortValues, state);
};

const getTableTitleFilterValues = (tableTitleFilters, data) => {
  const { filter, isEmptyValue } = data;

  const propToUse = ifElse(
    R.propEq('references', 'propertyName', filter),
    GC.FIELD_REFERENCE_NAME,
    'propertyName',
  );

  return ifElse(
    isEmptyValue,
    R.dissoc(R.prop(propToUse, filter), tableTitleFilters),
    R.assoc(R.prop(propToUse, filter), filter, tableTitleFilters),
  );
};

const setTableTitleFilter = (state, data) => {
  const { tableTitleFilters } = state;

  const filterValues = getTableTitleFilterValues(tableTitleFilters, data);

  return P.$set('tableTitleFilters', filterValues, state);
};

const getSearchCriteriaWithTitleFilters = (report, titleFilterParams, concatFilters = false) => {
  const reportSearchCriteria = getOrElse(report, 'searchCriteria', []);

  if (isNilOrEmpty(titleFilterParams)) return reportSearchCriteria;

  const titleFilters = R.values(titleFilterParams);

  if (R.or(concatFilters, hasNotAmousCurrentUserPermissions(PC.REPORT_WRITE))) {
    return R.concat(titleFilters, reportSearchCriteria);
  }

  return titleFilters;
};

const omitReportFields = (report, fieldNames = []) => R.assoc(
  'fields',
  R.compose(
    R.sortBy(R.prop('sequence')),
    R.values,
    R.omit(fieldNames),
    R.indexBy(R.prop(GC.FIELD_NAME)),
    R.prop('fields'),
  )(report),
  report,
);

const getAssessorialName = (name) => {
  const assessorials = R.indexBy(R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID), R.or(getItemFromWindow('assessorialList'), []));
  const splitted = R.split('.', name);
  const guid = splitted[R.dec(splitted.length)];

  return R.pathOr('', [guid, GC.FIELD_DISPLAYED_VALUE], assessorials);
};

const getReferenceTitleName = (name) => {
  let prefix = null;

  if (R.includes(`${GC.REFERENCE_FIELD_NAME_CLO}.`, name)) {
    prefix = GC.REFERENCE_FIELD_NAME_CLO;
  } else if (R.includes(`${GC.REFERENCE_FIELD_NAME_TEL}.`, name)) {
    prefix = GC.REFERENCE_FIELD_NAME_TEL;
  } else if (R.includes(GC.REFERENCE_FIELD_NAME_CLOS, name)) {
    prefix = GC.REFERENCE_FIELD_NAME_CLOS;
  } else if (R.includes(GC.REFERENCE_FIELD_NAME_TELS, name)) {
    prefix = GC.REFERENCE_FIELD_NAME_TELS;
  } else if (R.includes(GC.REFERENCE_FIELD_NAME_LOAD, name)) {
    prefix = GC.REFERENCE_FIELD_NAME_LOAD;
  }

  if (R.isNil(prefix)) return `Ref: ${name}`;

  return `Ref (${getWindowLocale(...GC.referenceFieldNameLocaleMap[prefix])}): ${R.last(R.split('.', name))}`;
};

const docTypePropMap = {
  [GC.FLEET_TRUCK_REPORT]: GC.TRUCK_DOCUMENT_TYPE,
  [GC.FLEET_DRIVER_REPORT]: GC.DRIVER_DOCUMENT_TYPE,
  [GC.FLEET_TRAILER_REPORT]: GC.TRAILER_DOCUMENT_TYPE,
};

const getCertificationTitleName = (name) => {
  const options = getAmousDropdownConfigOptionsByNameFromWindow(GC.DRIVER_CERTIFICATION_TYPE);
  const splitted = R.split('.', name);
  const guid = splitted[1];

  const { displayedValue } = R.find(R.propEq(guid, GC.FIELD_GUID), options);

  if (R.equals(splitted[2], 'expirationDate')) return `${displayedValue} Cert Exp Date`;

  return `${displayedValue} Cert Name`;
};

const getDocumentTitleName = (name, reportType) => {
  const options = getAmousDropdownConfigOptionsByNameFromWindow(docTypePropMap[reportType]);
  const splitted = R.split('.', name);
  const guid = splitted[1];

  const { displayedValue } = R.find(R.propEq(guid, GC.FIELD_GUID), options);

  if (R.equals(splitted[2], 'expirationDate')) return `${displayedValue} Doc Exp Date`;

  return `${displayedValue} Doc Name`;
};

const getFieldDisplayName = (field, fieldSettings) => {
  const { name, reference } = field;

  if (R.includes('Assessorial', name)) return `CHRG: ${getAssessorialName(name)}`;

  if (isTrue(reference)) return getReferenceTitleName(name);

  return R.pathOr(name, [name, GC.FIELD_NAME], fieldSettings);
};

const addDisplayNameToReportFields = (fields, fieldSettings) => {
  const indexedFieldSettings = R.indexBy(R.prop(GC.FIELD_VALUE), fieldSettings);

  return R.map(
    (field) => ({
      ...field,
      displayName: getFieldDisplayName(field, indexedFieldSettings),
    }),
    fields,
  );
};

const getReferencesForReport = (refList, prefix) => R.map(
  (ref) => R.mergeRight(ref, {
    name: `${prefix}.${R.prop(GC.FIELD_NAME, ref)}`,
    prefix: getWindowLocale(...GC.referenceFieldNameLocaleMap[prefix]),
  }),
  refList,
);

const getRefListByReportType = (refTypesByScopeName, type) => {
  const map = {
    [GC.CLO_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
    [GC.TEL_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
    [GC.USER_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_USER], refTypesByScopeName),
    [GC.ITEM_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_ITEM], refTypesByScopeName),
    [GC.TEL_EVENT_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
    [GC.ROUTE_CLO_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
    [GC.ROUTE_TEL_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
    [GC.PIVOT_CLO_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
    [GC.PIVOT_TEL_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
    [GC.BRANCH_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_BRANCH], refTypesByScopeName),
    [GC.CLO_BY_TEL_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
    [GC.CARRIER_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CARRIER], refTypesByScopeName),
    [GC.FLEET_TRUCK_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_TRUCK], refTypesByScopeName),
    [GC.CUSTOMER_PORTAL_CLO_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
    [GC.FLEET_DRIVER_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_DRIVER], refTypesByScopeName),
    [GC.FLEET_VENDOR_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_VENDOR], refTypesByScopeName),
    [GC.LOCATION_TEMPLATE_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_LOCATION], refTypesByScopeName),
    [GC.FLEET_TRAILER_REPORT]: () => R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_TRAILER], refTypesByScopeName),
    [GC.CARRIER_INVOICE_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_CARRIER_INVOICE], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_TEL,
      ),
    ),
    [GC.CUSTOMER_INVOICE_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_CUSTOMER_INVOICE], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_CLO,
      ),
    ),
    [GC.PIVOT_CUSTOMER_INVOICE_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_CUSTOMER_INVOICE], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_CLO,
      ),
    ),
    [GC.DRIVER_PAYROLL_INVOICE_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_INVOICE], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_TEL,
      ),
    ),
    [GC.VENDOR_INVOICE_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_FLEET_INVOICE], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_TEL,
      ),
    ),
    [GC.CLO_EVENT_REPORT]: () => R.concat(
      R.pathOr([], [GC.REF_SCOPE_NAME_LOAD_EVENT], refTypesByScopeName),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_CLO,
      ),
    ),
    [GC.ROUTE_REPORT]: () => R.concat(
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_TELS,
      ),
      getReferencesForReport(
        R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName),
        GC.REFERENCE_FIELD_NAME_CLOS,
      ),
    ),
    [GC.ROUTE_BY_LOAD_REPORT]: () => getReferencesForReport(
      R.compose(
        R.values,
        R.indexBy(R.prop(GC.FIELD_GUID)),
        R.concat(R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName)),
      )(R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName)),
      GC.REFERENCE_FIELD_NAME_LOAD,
    ),
    [GC.PIVOT_ROUTE_BY_LOAD_REPORT]: () => getReferencesForReport(
      R.compose(
        R.values,
        R.indexBy(R.prop(GC.FIELD_GUID)),
        R.concat(R.pathOr([], [GC.REF_SCOPE_NAME_TEL], refTypesByScopeName)),
      )(R.pathOr([], [GC.REF_SCOPE_NAME_CLO], refTypesByScopeName)),
      GC.REFERENCE_FIELD_NAME_LOAD,
    ),
  };

  return R.pathOr(() => [], [type], map)();
};

const hasPinnedReports = (reports) => isAnyTrue(
  ...R.map((report) => getPropFromObject(GC.FIELD_PINNED_REPORT, report), R.or(reports, [])),
);

const hasSearchCriteria = (report, filter) => R.or(
  isNotNilAndNotEmpty(filter),
  isNotNilAndNotEmpty(R.path(['searchCriteria'], report)),
);

const hasPinnedReportsAndSearchCriteria = (reports, report, filter) => (
  R.and(
    hasPinnedReports((reports)),
    hasSearchCriteria(report, filter),
  )
);

// helper for define checked elements on list
const setSelectedElement = (selectedList, list, event) => {
  let selected = [];

  if (R.and(R.equals(event.target.name, 'all'), event.target.checked)) {
    selected = R.concat(list, []);
  } else if (R.and(R.equals(event.target.name, 'all'), R.not(event.target.checked))) {
    selected = [];
  } else if (R.not(R.equals(event.target.name, 'all'))) {
    selected = ifElse(
      R.includes(event.target.name, selectedList),
      R.filter((entGuid) => (R.not(R.equals(entGuid, event.target.name))), selectedList),
      R.concat(selectedList, [event.target.name]),
    );
  }

  return selected;
};

// helper for added searchCriteria if using list filter
const getReferenceNameWithoutPrefix = (referenceName = '', referenceFieldName) => {
  if (R.isEmpty(referenceName)) return '';

  const referencePrefix = `Ref (${getWindowLocale(...GC.referenceFieldNameLocaleMap[referenceFieldName])}): `;

  if (R.includes(referencePrefix, referenceName)) return R.slice(referencePrefix.length, Infinity, referenceName);

  return referenceName;
};

const transformReferenceOrderFields = R.map(
  (data) => {
    const { reference, referenceFieldName } = data;

    if (R.and(isTrue(reference), isNotNilAndNotEmpty(referenceFieldName))) {
      const value = getReferenceNameWithoutPrefix(R.prop(GC.FIELD_NAME, data), referenceFieldName);

      return R.assoc(GC.FIELD_NAME, value, data);
    }

    return data;
  },
);

const transformReferences = R.map(
  (data) => {
    const { propertyName, referenceName, referenceFieldName } = data;

    if (R.and(R.equals(propertyName, GC.FIELD_REFERENCES), isNotNilAndNotEmpty(referenceFieldName))) {
      const value = getReferenceNameWithoutPrefix(referenceName, referenceFieldName);

      return R.assoc(GC.FIELD_REFERENCE_NAME, value, data);
    }

    return data;
  },
);

const setSearchCriteria = ({ filterParams, reqBody }) => ifElse(
  isNotNilAndNotEmpty(filterParams),
  () => R.assoc(
    'searchCriteria',
    transformReferences(R.concat(reqBody.searchCriteria, R.of(Array, filterParams))),
    reqBody,
  ),
  () => reqBody,
)();

// helper for compiling all frozen column
const getFixedColumn = (report, settings) => {
  let freezedColumn = [];

  if (isNotEmpty(report)) {
    freezedColumn = R.filter((field) => (
      R.and(notEquals(field.name, 'guid'), R.equals(field.freezed, true))
    ), report.fields);

    const renderFreezedColumn = freezedColumn.map((column) => ({
      customLogic: R.equals(column[GC.FIELD_REFERENCE], true)
        || R.equals(column.name, 'roles.name')
        || R.pathOr(false, [column.name, 'customLogic'], settings),
      label: getOrElse(settings[column.name], 'label', null),
      sequence: column.sequence,
      nameForAttribute: column.name,
      name: ifElse(
        isNotEmpty(column.name),
        getOrElse(settings[column.name], 'name', `Ref: ${column.name}`),
        '',
      ),
      width: getOrElse(settings[column.name], 'width', 300),
    }));

    return R.sort((a, b) => (
      a.sequence - b.sequence
    ), renderFreezedColumn);
  }
  return freezedColumn;
};

const getCustomLogic = (column, columnSettings) => {
  const customLogic = R.path([column.name, 'customLogic'], columnSettings);

  if (R.is(Function, customLogic)) {
    return customLogic;
  }

  return R.equals(column[GC.FIELD_REFERENCE], true)
  || R.equals(column.name, 'roles.name')
  || R.pathOr(false, [column.name, 'customLogic'], columnSettings);
};

// helper for compiling all free column
const getBasicColumn = (report, settings) => {
  let basicColumn = [];

  if (isNotEmpty(report)) {
    basicColumn = R.filter((field) => (
      R.and(
        notEquals(field.name, 'guid'),
        notEquals(field.freezed, true),
      )
    ), report.fields);

    const renderBasicColumn = basicColumn.map((column) => ({
      customLogic: getCustomLogic(column, settings),
      label: getOrElse(settings[column.name], 'label', null),
      sequence: column.sequence,
      nameForAttribute: column.name,
      name: ifElse(
        isNotEmpty(column.name),
        getOrElse(settings[column.name], 'name', `Ref: ${column.name}`),
        '',
      ),
      width: getOrElse(settings[column.name], 'width', 300),
    }));

    return R.sort((a, b) => (
      a.sequence - b.sequence
    ), renderBasicColumn);
  }

  return basicColumn;
};

// helper for setting filter by entity params
const setFilterByParams = (
  refList = [],
  entityParams = [],
  options = {},
) => {
  const reportType = R.propOr('', 'reportType', options);
  const assessorialList = R.propOr([], 'assessorialList', options);
  const disableRefFilter = R.propOr(false, 'disableRefFilter', options);

  const refs = R.map(({ name, prefix }) => {
    const prefixValue = ifElse(isNotNilAndNotEmpty(prefix), `Ref (${prefix})`, 'Ref');

    return {
      value: name,
      type: GC.FIELD_REFERENCE,
      disableFilter: disableRefFilter,
      name: `${prefixValue}: ${R.last(R.split('.', name))}`,
    };
  })(refList);

  const truckTrailerReports = [GC.FLEET_TRUCK_REPORT, GC.FLEET_TRAILER_REPORT];

  const isFleetReport = R.includes(reportType, [
    ...truckTrailerReports,
    GC.FLEET_DRIVER_REPORT,
  ]);

  if (isFleetReport) {
    const types = R.prop('fleetDocumentAndCertificationTypes', options);

    const documentTypeExpDates = R.compose(
      R.map(({ guid, displayedValue }) => ({
        name: `${displayedValue} Doc Exp Date`,
        value: `documentType.${guid}.expirationDate`,
      })),
      R.pathOr([], [docTypePropMap[reportType], 'options']),
    )(types);

    if (R.includes(reportType, truckTrailerReports)) return [...entityParams, ...refs, ...documentTypeExpDates];

    const certOptions = R.pathOr([], [GC.DRIVER_CERTIFICATION_TYPE, 'options'], types);

    let certFields = [];

    certOptions.forEach(({ guid, displayedValue }) => {
      certFields = [
        ...certFields,
        {
          name: `${displayedValue} Cert Name`,
          value: `certificationType.${guid}.certificationName.displayedValue`,
        },
        {
          name: `${displayedValue} Cert Exp Date`,
          value: `certificationType.${guid}.expirationDate`,
        },
      ];
    });

    return [...entityParams, ...refs, ...documentTypeExpDates, ...certFields];
  }

  const assessorialPrefixMap = {
    [GC.TEL_REPORT]: 'rate.sumByAssessorial.',
    [GC.CLO_REPORT]: 'rate.sumByAssessorial.',
    [GC.PIVOT_TEL_REPORT]: 'rate.sumByAssessorial.',
    [GC.PIVOT_CLO_REPORT]: 'rate.sumByAssessorial.',
    [GC.PAYROLL_REPORT]: 'invoices.sumByAssessorial.',
    [GC.CUSTOMER_INVOICE_REPORT]: 'sumByAssessorial.',
    [GC.PIVOT_CUSTOMER_INVOICE_REPORT]: 'sumByAssessorial.',
    [GC.VENDOR_PAYROLL_REPORT]: 'invoices.sumByAssessorial.',
    [GC.PIVOT_DRIVER_PAYROLL_REPORT]: 'invoices.sumByAssessorial.',
    [GC.PIVOT_VENDOR_PAYROLL_REPORT]: 'invoices.sumByAssessorial.',
  };

  const assessorialPrefix = R.pathOr('', [reportType], assessorialPrefixMap);

  const assessorials = R.map(
    ({ displayedValue, originalConfigGuid }) => ({
      type: 'assessorial',
      disableFilter: true,
      name: `CHRG: ${displayedValue}`,
      value: `${assessorialPrefix}${originalConfigGuid}`,
    }),
    assessorialList,
  );

  const payrollAssessorialPrefix = 'charges.sumByAssessorial.';

  const isPayrollReport = R.includes(reportType, [
    GC.PAYROLL_REPORT,
    GC.VENDOR_PAYROLL_REPORT,
    GC.PIVOT_DRIVER_PAYROLL_REPORT,
    GC.PIVOT_VENDOR_PAYROLL_REPORT,
  ]);

  if (R.not(isPayrollReport)) return [...entityParams, ...refs, ...assessorials];

  const payrollAssessorials = R.map(
    ({ displayedValue, originalConfigGuid }) => ({
      type: 'assessorial',
      disableFilter: true,
      name: `CHRG(Payroll): ${displayedValue}`,
      value: `${payrollAssessorialPrefix}${originalConfigGuid}`,
    }),
    assessorialList,
  );

  return [...entityParams, ...refs, ...assessorials, ...payrollAssessorials];
};

// helper for adding required field for reporting request
const addRequiredFields = (entity, requiredFields) => {
  let fields = entity.slice();

  requiredFields.forEach((requiredField) => {
    const exist = R.find(
      R.propEq(requiredField, GC.FIELD_NAME),
      entity,
    );

    if (R.isNil(exist)) {
      fields = R.append(
        {
          freezed: false,
          reference: false,
          name: requiredField,
          sequence: (fields.length + 1),
        },
        fields,
      );
    }
  });

  return fields;
};

const getReportSortedBySeqFreez = (report) => (
  R.assoc(
    'fields',
    R.sortBy(
      (field) => (
        R.not(R.prop('freezed', field))
      ),
      R.sortBy(
        R.prop('sequence'),
        report.fields,
      ),
    ),
    report,
  )
);

const getReportsSortedBySeqFreez = (reports) => (
  ifElse(
    isNilOrEmpty(reports),
    () => reports,
    () => R.map(
      getReportSortedBySeqFreez,
      reports,
    ),
  )()
);

const selectAll = (list, select) => (
  R.map((item) => (
    P.$set(
      'selected',
      select,
      item,
    )
  ), list)
);

const selectItem = (list, id) => (
  ifElse(
    R.equals(id, 'all'),
    selectAll(list, R.not(R.all((item) => isTrue(item.selected))(list))),
    R.map((item) => (
      P.$set(
        'selected',
        ifElse(
          R.equals(R.prop('guid', item), id),
          R.not(R.prop('selected', item)),
          R.prop('selected', item),
        ),
        item,
      )
    ), list),
  )
);

const isAllChecked = (itemList) => (
  ifElse(
    R.or(R.not(itemList), R.not(R.path(['length'], itemList))),
    () => false,
    () => R.all((item) => isTrue(item.selected))(itemList),
  )()
);

const findDefaultReport = (reports) => (
  R.or(
    reports.find((report) => isTrue(report.defaultReport)),
    R.prop(0, reports),
  )
);

const getGuidsFromStringOrSelectedList = (data, list) => (
  ifElse(
    R.is(String, data),
    () => R.of(Array, data),
    () => R.compose(
      R.values,
      R.map((entity) => entity.guid),
      R.filter((entity) => entity.selected),
    )(list),
  )()
);

const getSelectedItemGuids = (list) => R.compose(
  R.map(getGuidFromObject),
  R.filter(R.prop('selected')),
)(list);

const getTableSettingsWithMaxHeightByConditions = (props) => {
  const { reportList, filterParams, selectedReport, tableSettings, tableMinusHeight = 125 } = props;

  const hasPinned = hasPinnedReports(reportList);
  const hasFilters = hasSearchCriteria(selectedReport, filterParams);

  let maxHeight = `calc(100vh - ${tableMinusHeight}px)`;

  if (R.and(hasPinned, hasFilters)) {
    maxHeight = `calc(100vh - ${tableMinusHeight + 75}px)`;
  } else if (R.or(hasPinned, hasFilters)) {
    maxHeight = `calc(100vh - ${tableMinusHeight + 40}px)`;
  }

  return R.assoc('maxHeight', maxHeight, tableSettings);
};

const getFilterPropsFromColumnSettings = (columnSettings) => R.compose(
  R.indexBy(R.prop(GC.FIELD_VALUE)),
  R.values,
  R.mapObjIndexed(({ name, filter = { type: 'string' } }, value) => ({
    ...filter,
    value,
    // TODO: check where used name and this locale approach
    name: getWindowLocaleFromStringOrArray(name, ': '),
    // [GC.FIELD_NAME]: ifElse(
    //   isArray(name),
    //   `${getWindowLocale(name[0])}: ${getWindowLocale(name[1])}`,
    //   getWindowLocale(name),
    // ),
  })),
)(columnSettings);

const getReportFieldsFromColumnSettings = (columnSettings) => R.compose(
  mapIndexed(({ name, collection }, index) => ({ name, collection, sequence: index })),
  R.values,
  R.mapObjIndexed(({ collection }, value) => ({
    collection,
    name: value,
  })),
)(columnSettings);

const getOnlyReportFieldsFromColumnSettings = (columnSettings = []) => Object.keys(columnSettings)
  .map((name) => ({ name }));

const getReportFromColumnSettings = (columnSettings = [], additionProps = {}) => ({
  ...additionProps,
  fields: getOnlyReportFieldsFromColumnSettings(columnSettings),
});

const getAddressPointFieldsByType = (prefix) => {
  const localePrefix = ifElse(
    R.equals(prefix, GC.FIELD_ORIGIN),
    'titles:origin',
    'titles:destination',
  );

  const getLocaleArr = (key) => [localePrefix, key];

  return {
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_CITIES}`]: {
      width: 300,
      collection: true,
      name: getLocaleArr('titles:cities'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_STATES}`]: {
      width: 300,
      collection: true,
      fieldType: 'state',
      name: getLocaleArr('titles:states'),
      filterType: 'selectMultipleCreatable',
      options: getGroupedByCountryStateOptionsByConfig,
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_COUNTRIES}`]: {
      width: 300,
      collection: true,
      name: getLocaleArr('titles:countries'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES}`]: {
      width: 300,
      collection: true,
      name: getLocaleArr('titles:zip-codes'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO}`]: {
      name: getLocaleArr('titles:zip-to'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM}`]: {
      name: getLocaleArr('titles:zip-from'),
    },
  };
};

const getBooleanSearchCriteria = ({ propertyName, booleanValue }) => ({
  propertyName,
  booleanValue,
  operation: 'equal',
  dataType: 'boolean',
});

const getSearchCriteriaByGuidAndPropertyName = (guid, propertyName) => R.of(Array, {
  propertyName,
  stringValue: guid,
  dataType: 'string',
  operation: 'equal',
});

const getSearchCriteriaByGuid = (guid) => getSearchCriteriaByGuidAndPropertyName(guid, GC.FIELD_GUID);

const getAmousUiReportsFromLocalStorage = () => getItemFromLocalStorage('amousUiReports');

const setUiReportFieldsToLocalStorage = (reportName, fields) => setItemToLocalStorage(
  'amousUiReports',
  R.assoc(reportName, fields, R.or(getAmousUiReportsFromLocalStorage(), {})),
);

const filterOperationsMap = {
  in: ({ itemValue, filterValue }) => R.equals(itemValue, filterValue),
  equal: ({ itemValue, filterValue }) => R.equals(itemValue, filterValue),
  contain: ({ itemValue, filterValue }) => stringIncludesFilterValue(itemValue, filterValue),
  [GC.FIELD_DATE_RANGE]: ({ itemValue, filterValue }) => {
    const { to, from } = filterValue;

    return isBetween(itemValue, from, to, '[]');
  },
};

const getFilterValue = (filter) => {
  const { to, from, dataType, stringValue, numberValue, booleanValue } = filter;

  switch (dataType) {
    case 'string':
    case 'selectMultiple':
    case 'selectMultipleCreatable':
      return stringValue;
    case 'number':
      return isNotNilAndNotEmpty(numberValue) ? toNumber(numberValue) : numberValue;
    case 'date':
      return { to, from };
    case 'boolean':
      return booleanValue;
    default:
      return null;
  }
};

const getReportFieldNames = (report) => R.pluck(GC.FIELD_NAME, R.pathOr([], ['fields'], report));

const getItemListFilteredByUiTableTitleFilters = (
  report,
  itemList,
  uiTableTitleFilters,
  callbackData = {},
) => {
  if (isAnyNilOrEmpty([itemList, uiTableTitleFilters])) return itemList;

  const filtersKeys = R.keys(uiTableTitleFilters);
  const reportFieldNames = getReportFieldNames(report);

  return R.filter(
    (item) => R.all((filterKey) => {
      if (notContain(filterKey, reportFieldNames)) return true;

      const filter = getPropFromObject(filterKey, uiTableTitleFilters);
      const getFieldUiValue = R.propOr(null, 'getFieldUiValue', filter);
      const filterValue = getFilterValue(filter);

      let itemValue = R.pathOr(getPropFromObject(filterKey, item), R.split('.', filterKey), item);

      if (R.prop('collection', filter)) {
        itemValue = createStringFromArray(itemValue, ', ');
      }

      if (R.and(isNotNilAndNotEmpty(itemValue), isTrue(R.prop('shouldRound', filter)))) {
        itemValue = mathRoundNumber(itemValue);
      }

      if (isFunction(getFieldUiValue)) {
        itemValue = getFieldUiValue(item, callbackData);
      }

      const filterOperation = getPropFromObject(
        getPropFromObject(GC.FIELD_OPERATION, filter),
        filterOperationsMap,
      );

      return filterOperation ? filterOperation({ itemValue, filterValue }) : true;
    }, filtersKeys),
    itemList,
  );
};

const getItemListSortedByUITableSorting = (
  report,
  sortValues,
  filterProps,
  itemList,
  callbackData,
) => {
  const reportFieldNames = getReportFieldNames(report);

  const sortingList = R.compose(
    R.sortBy(R.prop('sequence')),
    R.filter(({ name }) => R.includes(name, reportFieldNames)),
    R.values,
  )(sortValues);

  const sortingCriteria = R.map((sortOption) => {
    const { name, order } = sortOption;

    const filterParams = R.prop(name, filterProps);

    if (R.not(filterParams)) return () => {};

    const { type, getFieldUiValue } = filterParams;

    const orderFn = ifElse(R.equals(order, GC.SORT_TYPE_DESC), R.descend, R.ascend);

    return orderFn((arg) => {
      const value = isFunction(getFieldUiValue) ? getFieldUiValue(arg, callbackData) : R.propOr('', name, arg);

      switch (type) {
        case 'date':
          if (R.equals(name, GC.FIELD_LOAD_BOARD_LAST_MODIFIED_DATE)) {
            return Math.abs(R.subtract(new Date(), new Date(value)));
          }

          return isNotNilAndNotEmpty(value) ? new Date(value).getTime() : 0;
        case 'number':
          return +(value || 0);
        case 'string':
          return R.and(isString(value), isNotEmpty(value)) ? R.toLower(value) : '';
        default:
          return value;
      }
    });
  }, sortingList);

  return R.sortWith(sortingCriteria, itemList);
};

const getSortedReport = (fieldNames, report) => ({
  ...report,
  fields: fieldNames.map((name, sequence) => ({ name, sequence })),
});

const handleSetCreatedByFilter = ({
  createdByFilter,
  setCreatedByFilter,
  getItemListRequest,
  resetListAndPagination,
}) => (createdBy) => {
  if (R.equals(createdBy, createdByFilter)) return;

  setCreatedByFilter(createdBy);
  resetListAndPagination();
  getItemListRequest(true);
};

const getSearchCriteriaWithCreatedByFilter = (
  createdBy,
  searchCriteria,
  propertyName = GC.FIELD_CREATED_BY,
) => {
  if (isNilOrEmpty(createdBy)) return searchCriteria;

  return R.append(
    {
      propertyName,
      dataType: 'string',
      [GC.FIELD_OPERATION]: 'in',
      [GC.FIELD_STRING_VALUE]: createdBy,
    },
    R.reject(R.propEq(propertyName, GC.FIELD_PROPERTY_NAME), searchCriteria),
  );
};

const docTypeExpDateRegex = /^documentType\..*\.expirationDate$/;
const certificationTypeExpDateRegex = /^certificationType\..*\.expirationDate$/;
const certificationTypeNameRegex = /^certificationType\..*\.certificationName.displayedValue$/;

const isFieldNameDocTypeExpDate = (name) => docTypeExpDateRegex.test(name);
const isFieldNameCertTypeExpDateOrName = (name) => R.or(
  certificationTypeNameRegex.test(name),
  certificationTypeExpDateRegex.test(name),
);

const dynamicFieldRegexList = [
  certificationTypeNameRegex,
  docTypeExpDateRegex,
  certificationTypeExpDateRegex,
];

const isDynamicField = (name) => R.any((regExp) => regExp.test(name), dynamicFieldRegexList);

const getFieldTypeAndSearchable = (name) => ({
  searchable: isDynamicField(name),
  type: R.any((regExp) => regExp.test(name), R.tail(dynamicFieldRegexList)) ? 'date' : 'string',
});

export {
  selectAll,
  selectItem,
  isAllChecked,
  getFixedColumn,
  getBasicColumn,
  isDynamicField,
  getSortedReport,
  omitReportFields,
  hasPinnedReports,
  setTableTitleSort,
  setSearchCriteria,
  addRequiredFields,
  hasSearchCriteria,
  setFilterByParams,
  findDefaultReport,
  setSelectedElement,
  getAssessorialName,
  isFilterNumberRange,
  isFilterStringRange,
  setTableTitleFilter,
  getSelectedItemGuids,
  getDocumentTitleName,
  getReferenceTitleName,
  getDefaultReportModal,
  isFilterValuesBoolean,
  handleTableTitleFilter,
  getRefListByReportType,
  getSearchCriteriaByGuid,
  getTableTitleSortValues,
  handleTableTitleFilter2,
  handleSetCreatedByFilter,
  getBooleanSearchCriteria,
  getReportSortedBySeqFreez,
  getTableTitleFilterValues,
  localeKeyToReportFieldMap,
  getCertificationTitleName,
  getFieldTypeAndSearchable,
  isFieldNameDocTypeExpDate,
  getReportsSortedBySeqFreez,
  getReportFromColumnSettings,
  getAddressPointFieldsByType,
  addDisplayNameToReportFields,
  addSystemFieldsToTableReport,
  setOptionLabelFromNameOrValue,
  transformReferenceOrderFields,
  setUiReportFieldsToLocalStorage,
  getReportColumnPropTextFromData,
  prependFieldToTableReportFields,
  getGuidsFromStringOrSelectedList,
  getFilterPropsFromColumnSettings,
  isFieldNameCertTypeExpDateOrName,
  getItemListSortedByUITableSorting,
  hasPinnedReportsAndSearchCriteria,
  createReportFilterFromTitleSearch,
  getReportFieldsFromColumnSettings,
  getSearchCriteriaWithTitleFilters,
  getSearchCriteriaWithCreatedByFilter,
  getOnlyReportFieldsFromColumnSettings,
  getSearchCriteriaByGuidAndPropertyName,
  getItemListFilteredByUiTableTitleFilters,
  getTableSettingsWithMaxHeightByConditions,
};
